import { ReactNode, useEffect, useMemo, useState } from "react"
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Clock,
  FileDown,
  Calendar,
  Sparkles,
  Wallet,
  BarChart3,
  ArrowUpRight,
} from "lucide-react"
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  AreaChart,
  Area,
} from "recharts"
import * as XLSX from "xlsx"
import jsPDF from "jspdf"
import { useDispatch, useSelector } from "react-redux"
import { reportsData, generalReportsData } from "../../store/actions"
import SubscriptionModal from "../../components/subscrptions/SubscriptionModal"
import { hasFeature, getCurrentPlan } from "../../utils/permissions"
import { runSubscriptionUpgrade } from "../../utils/subscriptionUpgrade"
import { isCatalogStore, isCarStore as isCarStoreCategory, isPropertyStore as isPropertyStoreCategory } from "../../utils/storeCategory"
import { getStoreExperience } from "../../utils/storeExperience"

type Period = "DAY" | "MONTH" | "YEAR"

type ChartRow = {
  date: string
  encaisse: number
  dette: number
  montant: number
  profit: number
}

export default function Reports() {
  const reportsdata = useSelector((state: any) => state.UserReducer.reportsdata || {})
  const generalreportsdata = useSelector((state: any) => state.UserReducer.generalreportsdata || {})
  const userdata = useSelector((state: any) => state.UserReducer.userdata)
  const currentPlan = getCurrentPlan(userdata)
  const hasAdvancedReports = hasFeature(currentPlan, "ADVANCED_REPORTS")
  const availablePlans = userdata?.availablePlans
  const currentBillingCycle = userdata?.subscription?.billingCycle || userdata?.user?.subscriptionBillingCycle || "monthly"
  const { user, store } = userdata
  const dispatch = useDispatch()
  const experience = getStoreExperience(store)
  const isGeneralStore = isCatalogStore(store)
  const isCarStore = isCarStoreCategory(store)
  const isPropertyStore = isPropertyStoreCategory(store)

  const [period, setPeriod] = useState<Period>("MONTH")
  const [selectedMonth, setSelectedMonth] = useState<string>("")
  const [openSubscription, setOpenSubscription] = useState(false)

  const reports = useMemo(
    () => (isGeneralStore ? generalreportsdata || {} : reportsdata || {}),
    [isGeneralStore, generalreportsdata, reportsdata]
  )

  const storeId = store?._id

  useEffect(() => {
    if (!hasAdvancedReports || !storeId) return

    const params: any = {
      period,
      page: 1,
      limit: 50,
    }

    if (selectedMonth) {
      params.month = selectedMonth
    }

    if (isGeneralStore) {
      dispatch(generalReportsData(storeId, params))
    } else {
      dispatch(reportsData(storeId, params))
    }
  }, [dispatch, storeId, period, selectedMonth, isGeneralStore, hasAdvancedReports])

  const reportCopy = useMemo(() => {
    if (isPropertyStore) {
      return {
        badge: "Finance agence",
        title: "Rapports financiers immobilier",
        subtitle: "Suivez les encaissements, loyers, dépenses et marges depuis une vue plus premium.",
      }
    }

    if (isCarStore) {
      return {
        badge: "Finance parc auto",
        title: "Rapports financiers auto",
        subtitle: "Analysez les encaissements, dépenses et performances de votre showroom dans un cockpit plus net.",
      }
    }

    return {
      badge: "Pilotage financier",
      title: "Rapports financiers",
      subtitle: "Une lecture plus claire des ventes, encaissements, dettes et bénéfices.",
    }
  }, [isCarStore, isPropertyStore])

  const normalizedCharts = useMemo(() => {
    const salesRows = Array.isArray(reports?.charts?.sales) ? reports.charts.sales : []
    const expenseRows = Array.isArray(reports?.charts?.expenses) ? reports.charts.expenses : []
    const profitRows = Array.isArray(reports?.charts?.profit) ? reports.charts.profit : []
    const byDate = new Map<string, ChartRow>()

    const ensureRow = (rawDate: any) => {
      const date = String(rawDate || "-")
      if (!byDate.has(date)) {
        byDate.set(date, { date, encaisse: 0, dette: 0, montant: 0, profit: 0 })
      }
      return byDate.get(date)!
    }

    salesRows.forEach((row: any) => {
      const entry = ensureRow(row?.date || row?._id?.day || row?._id?.month || row?._id || "-")
      entry.encaisse = Number(row?.encaissé ?? row?.encaisse ?? row?.paid ?? row?.amountPaid ?? 0)
      entry.dette = Number(row?.dette ?? row?.debt ?? row?.remaining ?? 0)
    })

    expenseRows.forEach((row: any) => {
      const entry = ensureRow(row?.date || row?._id?.day || row?._id?.month || row?._id || "-")
      entry.montant = Number(row?.montant ?? row?.amount ?? row?.total ?? 0)
    })

    profitRows.forEach((row: any) => {
      const entry = ensureRow(row?.date || row?._id?.day || row?._id?.month || row?._id || "-")
      entry.profit = Number(row?.profit ?? row?.amount ?? 0)
    })

    return Array.from(byDate.values())
  }, [reports])

  const financeStats = useMemo(() => {
    const kpis = reports.kpis || {
      totalSales: 0,
      totalPaid: 0,
      totalDebt: 0,
      totalExpenses: 0,
      profit: 0,
    }
    const totalSales = Number(kpis.totalSales || 0)
    const totalPaid = Number(kpis.totalPaid || 0)
    const totalDebt = Number(kpis.totalDebt || 0)
    const totalExpenses = Number(kpis.totalExpenses || 0)
    const profit = Number(kpis.profit || 0)

    return {
      totalSales,
      totalPaid,
      totalDebt,
      totalExpenses,
      profit,
      collectionRate: totalSales > 0 ? (totalPaid / totalSales) * 100 : 0,
      debtWeight: totalSales > 0 ? (totalDebt / totalSales) * 100 : 0,
      cashAfterExpenses: totalPaid - totalExpenses,
    }
  }, [reports])

  const exportExcel = () => {
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet([
        {
          Boutique: store?.name || "-",
          Periode: selectedMonth || period,
          Ventes: financeStats.totalSales,
          Encaisse: financeStats.totalPaid,
          Dettes: financeStats.totalDebt,
          Depenses: financeStats.totalExpenses,
          Benefice: financeStats.profit,
          TauxEncaissement: `${financeStats.collectionRate.toFixed(1)}%`,
        },
      ]),
      "Synthese"
    )
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(normalizedCharts), "Courbes")
    XLSX.writeFile(workbook, "rapport_gestock.xlsx")
  }

  const exportPDF = () => {
    const pdf = new jsPDF()
    pdf.setFontSize(18)
    pdf.text("Rapport financier GESTOCK", 14, 20)
    pdf.setFontSize(11)
    pdf.text(`Boutique: ${store?.name || "-"}`, 14, 32)
    pdf.text(`Periode: ${selectedMonth || period}`, 14, 40)
    pdf.text(`Total ventes: ${financeStats.totalSales.toLocaleString()} FCFA`, 14, 54)
    pdf.text(`Total encaisse: ${financeStats.totalPaid.toLocaleString()} FCFA`, 14, 62)
    pdf.text(`Total dettes: ${financeStats.totalDebt.toLocaleString()} FCFA`, 14, 70)
    pdf.text(`Total depenses: ${financeStats.totalExpenses.toLocaleString()} FCFA`, 14, 78)
    pdf.text(`Benefice: ${financeStats.profit.toLocaleString()} FCFA`, 14, 86)
    pdf.text(`Taux d'encaissement: ${financeStats.collectionRate.toFixed(1)}%`, 14, 94)
    pdf.save("rapport_gestock.pdf")
  }

  if (!hasAdvancedReports) {
    return (
      <>
        <div className="max-w-3xl rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <p className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-[11px] uppercase tracking-[0.16em] text-blue-700">
            <Sparkles size={12} /> Upgrade requis
          </p>
          <h1 className="mt-4 text-2xl font-bold text-slate-800">Rapports financiers</h1>
          <p className="mt-2 text-slate-600">
            Les rapports avancés sont disponibles avec les plans PRO et BUSINESS.
          </p>
          <button
            onClick={() => setOpenSubscription(true)}
            className="mt-5 rounded-2xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-blue-700"
          >
            Mettre à niveau mon abonnement
          </button>
        </div>
        <SubscriptionModal
          isOpen={openSubscription}
          currentPlan={currentPlan}
          currentBillingCycle={currentBillingCycle}
          plans={availablePlans}
          onClose={() => setOpenSubscription(false)}
          onUpgrade={(planKey, billingCycle) =>
            runSubscriptionUpgrade({
              planKey,
              billingCycle,
              currentPlan,
              currentBillingCycle,
              user,
              dispatch,
              onClose: () => setOpenSubscription(false),
            })
          }
        />
      </>
    )
  }

  return (
    <div className="space-y-8">
      <section className={`relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br p-6 text-white shadow-xl sm:p-7 ${experience.stock.heroClass}`}>
        <div className={`pointer-events-none absolute -top-12 -right-10 h-36 w-36 rounded-full blur-3xl ${experience.stock.heroGlowClass}`} />
        <div className="pointer-events-none absolute -bottom-10 -left-8 h-32 w-32 rounded-full bg-white/10 blur-3xl" />

        <div className="relative flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
          <div className="max-w-2xl">
            <p className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 text-[11px] uppercase tracking-[0.16em]">
              <Sparkles size={12} /> {reportCopy.badge}
            </p>
            <h1 className="mt-3 text-2xl font-bold sm:text-3xl">{reportCopy.title}</h1>
            <p className="mt-2 text-sm text-white/85">{reportCopy.subtitle}</p>

            <div className="mt-5 flex flex-wrap gap-2">
              <ActionButton onClick={exportExcel} label="Exporter Excel" solid />
              <ActionButton onClick={exportPDF} label="Exporter PDF" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:max-w-sm">
            <HeroMetric label="Encaisse net" value={`${financeStats.cashAfterExpenses.toLocaleString()} FCFA`} />
            <HeroMetric label="Taux encaissement" value={`${financeStats.collectionRate.toFixed(1)}%`} />
          </div>
        </div>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-sm font-semibold text-slate-800">Filtres de lecture</p>
            <p className="mt-1 text-xs text-slate-500">Changez l’horizon pour relire vos performances selon le bon rythme.</p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {[
              { label: "Aujourd'hui", value: "DAY" },
              { label: "Ce mois", value: "MONTH" },
              { label: "Cette année", value: "YEAR" },
            ].map((item) => (
              <button
                key={item.value}
                onClick={() => {
                  setPeriod(item.value as Period)
                  setSelectedMonth("")
                }}
                className={`rounded-full px-4 py-2 text-sm font-medium transition ${
                  period === item.value && !selectedMonth
                    ? "bg-slate-900 text-white"
                    : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                }`}
              >
                {item.label}
              </button>
            ))}

            <div className="relative">
              <Calendar size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => {
                  setSelectedMonth(e.target.value)
                  setPeriod("MONTH")
                }}
                className="rounded-2xl border border-slate-200 py-2 pl-9 pr-3 text-sm text-slate-700 outline-none focus:ring-4 focus:ring-slate-100"
              />
            </div>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard label="Ventes" value={financeStats.totalSales} icon={<TrendingUp size={18} />} gradient="from-blue-600 to-cyan-500" />
        <KpiCard label="Encaisse" value={financeStats.totalPaid} icon={<DollarSign size={18} />} gradient="from-emerald-600 to-green-500" />
        <KpiCard label="Dettes" value={financeStats.totalDebt} icon={<Clock size={18} />} gradient="from-amber-600 to-orange-500" />
        <KpiCard label="Depenses" value={financeStats.totalExpenses} icon={<TrendingDown size={18} />} gradient="from-rose-600 to-red-500" />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <MetricPanel
          title="Rythme d'encaissement"
          value={`${financeStats.collectionRate.toFixed(1)}%`}
          hint="Part des ventes déjà encaissées"
          icon={<Wallet size={18} />}
          tone={financeStats.collectionRate >= 70 ? "success" : "warning"}
        />
        <MetricPanel
          title="Poids des dettes"
          value={`${financeStats.debtWeight.toFixed(1)}%`}
          hint="Part des ventes encore non réglées"
          icon={<Clock size={18} />}
          tone={financeStats.debtWeight <= 20 ? "success" : "danger"}
        />
        <MetricPanel
          title="Cash après dépenses"
          value={`${financeStats.cashAfterExpenses.toLocaleString()} FCFA`}
          hint="Encaissement moins dépenses"
          icon={<ArrowUpRight size={18} />}
          tone={financeStats.cashAfterExpenses >= 0 ? "success" : "danger"}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <ChartCard
          title="Encaissements & dettes"
          subtitle="Lecture parallèle de la trésorerie encaissée et des restes clients."
          badge="Flux clients"
        >
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={normalizedCharts}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" stroke="#64748b" />
              <YAxis stroke="#64748b" />
              <Tooltip />
              <Line type="monotone" dataKey="encaisse" stroke="#16a34a" strokeWidth={3} dot={false} />
              <Line type="monotone" dataKey="dette" stroke="#ea580c" strokeWidth={3} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Depenses"
          subtitle="Vision claire des sorties de caisse sur la période active."
          badge="Charges"
        >
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={normalizedCharts}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" stroke="#64748b" />
              <YAxis stroke="#64748b" />
              <Tooltip />
              <Bar dataKey="montant" fill="#dc2626" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <ChartCard
        title="Evolution du benefice"
        subtitle="Courbe synthétique du résultat sur la période sélectionnée."
        badge="Marge"
      >
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={normalizedCharts}>
            <defs>
              <linearGradient id="profitFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#2563eb" stopOpacity={0.32} />
                <stop offset="95%" stopColor="#2563eb" stopOpacity={0.03} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="date" stroke="#64748b" />
            <YAxis stroke="#64748b" />
            <Tooltip />
            <Area type="monotone" dataKey="profit" stroke="#2563eb" strokeWidth={3} fill="url(#profitFill)" />
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>

      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="inline-flex items-center gap-2 text-sm font-semibold text-slate-800">
              <BarChart3 size={16} />
              Resume global
            </p>
            <p className="mt-1 text-sm text-slate-500">
              Une synthèse rapide pour la décision, sans quitter la page.
            </p>
          </div>
          <div className={`rounded-2xl px-4 py-3 text-sm font-semibold ${financeStats.profit >= 0 ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
            Benefice reel: {financeStats.profit.toLocaleString()} FCFA
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
          <SummaryCell label="Ventes brutes" value={`${financeStats.totalSales.toLocaleString()} FCFA`} />
          <SummaryCell label="Encaissement confirme" value={`${financeStats.totalPaid.toLocaleString()} FCFA`} />
          <SummaryCell label="Cash net apres depenses" value={`${financeStats.cashAfterExpenses.toLocaleString()} FCFA`} />
        </div>
      </section>
    </div>
  )
}

function KpiCard({
  label,
  value,
  icon,
  gradient,
}: {
  label: string
  value: number
  icon: ReactNode
  gradient: string
}) {
  return (
    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
      <div className={`flex items-center justify-between bg-gradient-to-r px-4 py-4 text-white ${gradient}`}>
        <div className="rounded-2xl bg-white/15 p-2.5">{icon}</div>
        <Sparkles size={16} className="text-white/80" />
      </div>
      <div className="px-4 py-4">
        <p className="text-xs uppercase tracking-[0.12em] text-slate-500">{label}</p>
        <p className="mt-2 text-2xl font-semibold text-slate-800">{value.toLocaleString()} FCFA</p>
      </div>
    </div>
  )
}

function MetricPanel({
  title,
  value,
  hint,
  icon,
  tone,
}: {
  title: string
  value: string
  hint: string
  icon: ReactNode
  tone: "success" | "warning" | "danger"
}) {
  const toneClass =
    tone === "success"
      ? "border-emerald-200 bg-emerald-50"
      : tone === "warning"
        ? "border-amber-200 bg-amber-50"
        : "border-rose-200 bg-rose-50"

  return (
    <div className={`rounded-3xl border p-5 shadow-sm ${toneClass}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-slate-800">{title}</p>
          <p className="mt-1 text-xs text-slate-500">{hint}</p>
        </div>
        <div className="rounded-2xl bg-white/70 p-2.5 text-slate-700">{icon}</div>
      </div>
      <p className="mt-4 text-2xl font-bold text-slate-900">{value}</p>
    </div>
  )
}

function HeroMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-white/20 bg-white/10 px-4 py-3 backdrop-blur-sm">
      <p className="text-xs uppercase tracking-[0.12em] text-white/70">{label}</p>
      <p className="mt-1 text-xl font-semibold">{value}</p>
    </div>
  )
}

function ChartCard({
  title,
  subtitle,
  badge,
  children,
}: {
  title: string
  subtitle: string
  badge: string
  children: ReactNode
}) {
  return (
    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 bg-gradient-to-r from-white to-slate-50 px-5 py-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-sm font-semibold text-slate-800">{title}</p>
            <p className="mt-1 text-xs text-slate-500">{subtitle}</p>
          </div>
          <span className="inline-flex rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-600">
            {badge}
          </span>
        </div>
      </div>
      <div className="p-4 sm:p-5">{children}</div>
    </div>
  )
}

function SummaryCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <p className="text-xs uppercase tracking-[0.12em] text-slate-500">{label}</p>
      <p className="mt-2 text-lg font-semibold text-slate-800">{value}</p>
    </div>
  )
}

function ActionButton({
  onClick,
  label,
  solid = false,
}: {
  onClick: () => void
  label: string
  solid?: boolean
}) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-medium transition ${
        solid
          ? "bg-white text-slate-900 hover:bg-slate-100"
          : "border border-white/20 bg-white/10 text-white hover:bg-white/15"
      }`}
    >
      <FileDown size={16} />
      {label}
    </button>
  )
}
